import React, { useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { useCookies } from 'react-cookie';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';

import setAuthToken from '../../shared/utils/setAuthToken';
import PrivateRoute from './PrivateRoute';
import LoginRoute from './LoginRoute';
import FourOhFourRoute from './FourOhFourRoute';
import BuildUnavailableRoute from './BuildUnavailableRoute';
import history from '../History/history';
import Login from '../../modules/login/login';
import Portal from '../../modules/login/portal';
import LoginWithSSOToken from '../../modules/login/components/presentational/sso/LoginWithSSOToken';
import LoginFormSpear from '../../modules/login/components/presentational/spear/LoginFormSpear';
import WeightroomView from '../../modules/weightroom-view/components/container/WeightroomViewContainer';
import WeightroomViewNew from '../../modules/weightroom-view-new/components/container/WeightroomViewContainer';
import TestPage from '../../modules/test-page/TestPage';
import Journal from '../../modules/journal/Journal';
import Documents from '../../modules/documents/Documents';
import Playground from '../../modules/playground/Playground';
// Legacy calendar management page now superseded by /dashboard/calendars for homeNavigator accounts
import Calendars from '../../modules/calendars/Calendars';
import Calendar from '../../modules/calendar/Calendar';
import CalendarCreate from '../../modules/calendar/CalendarCreate';
import AIProgramImport from '../../modules/ai-program-import/AIProgramImport';
import Profile from '../../modules/profile/Profile';
import Settings from '../../modules/settings/Settings';
import Leaderboard from '../../modules/leaderboard/Leaderboard';
import Reporting from '../../modules/reporting/Reporting';
import Reseller from '../../modules/reseller/Reseller';
import Programs from '../../modules/programs/Programs';
import Feed from '../../modules/feed/Feed';
import Conversations from '../../modules/conversations/Conversations';
import SocketTester from '../../shared/components/SocketTester/SocketTester';
import AccountSettingsPage from '../../modules/settings/components/presentational/account-settings/accountSettingsPage';
import OrganizationSettingsPage from '../../modules/settings/components/presentational/organization-settings/OrganizationSettingsPage';
import IntegrationsSettingsPage from '../../modules/settings/components/presentational/integrations-settings/IntegrationsSettingsPage';
import SubscriptionsSettingsPage from '../../modules/settings/components/presentational/subscriptions-settings/SubscriptionsSettingsPage';
import JoinOrganization from '../../modules/join-organization/JoinOrganization';
import ForgotPassword from '../../modules/forgot-password/ForgotPassword';
import ResetPassword from '../../modules/forgot-password/ResetPassword';
import SharedWorkout from '../../modules/shared-workout/components/SharedWorkout';
import Workouts from '../../modules/workouts/Workouts';
import SportsScience from '../../modules/sports-science/SportsScience';
import GymStudio from '../../modules/gymstudio/GymStudio';
import Evaluations from '../../modules/evaluations/Evaluations';
import TacticalSettingsPage from '../../modules/settings/components/presentational/tactical-settings/tacticalSettingsPage';
import { AthleteReportCardRouteWrapper } from '../../modules/athleteReportCard/AthleteReportCard';
import ViewOnlySCDash from '../../modules/sport-coach-dashboard/ViewOnlySCDash';
import ViewOnlyFPDash from '../../modules/force-plate-dashboard/ViewOnlyFPDashboard';
import ViewOnlyVTDash from '../../modules/volume-tracker-dashboard/ViewOnlyVTDashboard';
import CancelPage from '../../modules/settings/components/presentational/cancel-page';
import ViewOnlyLMDash from '../../modules/load-monitoring-dashboard/ViewOnlyLMDashboard';
import ViewOnlyVBTDash from '../../modules/vbt-dashboard/ViewOnlyVBTDashboard';
import DailyBrief from '../../modules/daily-brief/DailyBrief';
import Planner from '../../modules/planner/Planner';
import Registration from '../../modules/registration/Registration';
import Navigator from '../../modules/navigator/Navigator';
import AcceptAgreement from '../../modules/accept-agreement/AcceptAgreement';
import EasyJoin from '../../modules/easy-join/EasyJoin';

// Redirects for sports-science to ams and related module renaming
const REDIRECT_MAPPING = {
  'sport-coach-dashboard': 'kpi-tracker',
  'recovery-radar': 'body-heatmap',
  'consistency-coach': 'habit-tracker',
};

const Routes = ({ flags }) => {
  const [cookies, setCookies] = useCookies(['v2token']);
  const isAuthenticatedv3 = cookies.v2token !== undefined
    && cookies.v2token !== '0'
    && ((cookies.accessToken !== undefined && cookies.accessToken !== '0')
      || (cookies.refreshToken !== undefined && cookies.refreshToken !== '0'))
    && cookies.session_id !== undefined
    && cookies.session_id !== '0'
    && cookies.session_key !== undefined
    && cookies.session_key !== '0';

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isAccountTbAdmin = !!Object.keys(currentUser).length && (process.env.TB_ADMINS?.split(',') ?? []).includes(`${currentUser.id}`);

  // we assume that the user is registered until we get the currentUser object
  // and then if it's not registered we send them to the registration page
  // within the private route
  let isRegistered = true;

  if (Object.keys(currentUser).length && currentUser.isRegistered === false) {
    isRegistered = false;
  }

  let acceptAgreement = true;

  if (Object.keys(currentUser).length
    && (!isAccountTbAdmin && currentUser.organizationHasValidAgreement === false
      && currentUser.primaryAdmin === 1)) {
    acceptAgreement = false;
  }

  const isAuthenticated = cookies.v2token && cookies.v2token !== '0';

  useLayoutEffect(() => {
    if (cookies.v2token) {
      if (cookies.v2token !== '0') {
        setAuthToken(cookies.v2token);
      }
    }
  }, []);

  return (
    <ConnectedRouter history={history}>
      <Switch>
        <PrivateRoute
          exact
          path='/weightroom-view-old'
          component={WeightroomView}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticated}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/weightroom-view'
          component={WeightroomViewNew}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticated}
          registered={isRegistered}
        />
        <Route path='/programs' component={Programs} />
        <PrivateRoute
          path='/documents'
          component={Documents}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        {(flags && flags.calendarV2) || process.env.ENVIRONMENT === 'LOCAL' ? (
          <PrivateRoute
            path='/calendar/create'
            component={CalendarCreate}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        ) : null}
        {(flags && flags.calendarV2) || process.env.ENVIRONMENT === 'LOCAL' ? (
          <PrivateRoute
            path='/program/create'
            component={CalendarCreate}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        ) : null}
        {flags && flags.aiProgramImport && (
          <PrivateRoute
            path='/import/program'
            component={AIProgramImport}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        {(flags && flags.calendarV2) || process.env.ENVIRONMENT === 'LOCAL' ? (
          <PrivateRoute
            path='/build'
            component={Calendar}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        ) : (
          <Route
            path='/build'
            component={BuildUnavailableRoute}
          />
        )}
        {flags && flags.planner && (
          <PrivateRoute
            path='/planner'
            component={Planner}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        <PrivateRoute
          path='/playground'
          component={Playground}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          path='/profile'
          component={Profile}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings'
          component={Settings}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/account'
          component={AccountSettingsPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/organization'
          component={OrganizationSettingsPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/subscriptions'
          component={SubscriptionsSettingsPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/tactical'
          component={TacticalSettingsPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/integrations'
          component={IntegrationsSettingsPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/settings/cancel'
          component={CancelPage}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          path='/workouts'
          component={Workouts}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        {/* Legacy calendar management page. Accounts with homeNavigator access get new
            Calendars tab on Home instead — redirect them there (preserving sub-paths/query,
            e.g. ?editCalendarId=). Accounts without the flag still get the old page. */}
        {flags && flags.homeNavigator ? (
          <Route
            path='/calendars'
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: location.pathname.replace(/^\/calendars/, '/dashboard/calendars'),
                  search: location.search,
                }}
                status={301}
              />
            )}
          />
        ) : (
          <PrivateRoute
            path='/calendars'
            component={Calendars}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        <PrivateRoute
          path='/messaging'
          component={Conversations}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          path='/reseller'
          component={Reseller}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/reporting'
          component={Reporting}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/leaderboard'
          component={Leaderboard}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        {flags && flags.homeNavigator && (
          <Route exact path='/daily-brief' render={() => <Redirect to='/dashboard/daily-brief' />} />
        )}
        {(!flags || !flags.homeNavigator) && flags && flags.dailyBrief && (
          <PrivateRoute
            exact
            path='/dashboard'
            component={DailyBrief}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        {(!flags || !flags.homeNavigator) && (!flags || !flags.dailyBrief) && (
          <Route
            exact
            path='/dashboard'
            render={() => {
              window.location.href = `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/dashboard`;
              return null;
            }}
          />
        )}
        {flags && flags.homeNavigator && (
          <Route
            path='/dashboard'
            render={() => (
              currentUser?.admin === 0
                ? <Redirect to='/workouts' />
                : (
                  <PrivateRoute
                    path='/dashboard'
                    component={Navigator}
                    acceptAgreement={acceptAgreement}
                    authed={isAuthenticatedv3}
                    registered={isRegistered}
                  />
                )
            )}
          />
        )}
        <PrivateRoute
          path='/ams'
          component={SportsScience}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <PrivateRoute
          exact
          path='/join'
          component={JoinOrganization}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        {flags && flags.homeNavigator && (
          <PrivateRoute
            exact
            path='/easy-join'
            component={EasyJoin}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        <PrivateRoute
          path='/teambuildr-os'
          component={GymStudio}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <Route
          path='/gymstudio'
          render={() => <Redirect to='/teambuildr-os' />}
        />
        <Route path='/report-card' component={AthleteReportCardRouteWrapper} />
        <Route exact path='/kpi-tracker' component={ViewOnlySCDash} />
        <Route exact path='/lm-dash' component={ViewOnlyLMDash} />
        <Route exact path='/fp-dash' component={ViewOnlyFPDash} />
        <Route exact path='/vt-dash' component={ViewOnlyVTDash} />
        <Route exact path='/vbt-dash' component={ViewOnlyVBTDash} />
        <PrivateRoute
          path='/evaluations'
          component={Evaluations}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        {flags.feed && (
          <PrivateRoute exact path='/feed' component={Feed} acceptAgreement={acceptAgreement} authed={isAuthenticatedv3} registered={isRegistered} />
        )}
        {flags.journal && (
          <PrivateRoute exact path='/journal' component={Journal} acceptAgreement={acceptAgreement} authed={isAuthenticatedv3} registered={isRegistered} />
        )}
        <PrivateRoute
          path='/accept-agreement'
          component={AcceptAgreement}
          acceptAgreement={acceptAgreement}
          authed={isAuthenticatedv3}
          registered={isRegistered}
        />
        <LoginRoute
          exact
          path='/login'
          component={Login}
          authed={isAuthenticatedv3}
        />
        <LoginRoute
          path='/portal'
          component={Portal}
          authed={isAuthenticatedv3}
        />
        <LoginRoute
          exact
          path='/forgot'
          component={ForgotPassword}
          authed={isAuthenticatedv3}
        />
        {flags.registration && (
          <Route
            path='/signup/idnty'
            component={Registration}
          />
        )}
        {flags.registration && (
          <LoginRoute
            path='/signup'
            component={Registration}
            authed={isAuthenticatedv3}
          />
        )}
        <Route path='/reset-password' component={ResetPassword} />
        {/* Set Password route will use ResetPassword since the goal is the same */}
        {/* Only changing the titles and messaging a bit */}
        {flags.registration && <Route path='/set-password' component={ResetPassword} />}
        <Route exact path='/login/sso' component={LoginWithSSOToken} />
        <Route exact path='/login/spear' component={LoginFormSpear} />
        {process.env.ENVIRONMENT !== 'PRODUCTION' && (
          <PrivateRoute
            path='/socket-tester'
            component={SocketTester}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        {process.env.ENVIRONMENT !== 'PRODUCTION' && (
          <PrivateRoute
            exact
            path='/test-page'
            component={TestPage}
            acceptAgreement={acceptAgreement}
            authed={isAuthenticatedv3}
            registered={isRegistered}
          />
        )}
        <Route path='/' exact render={() => <Redirect to='/login' />} />
        {/* Added redirect to messaging from conversations after changing URLs. */}
        <Route
          path='/conversations'
          exact
          render={() => <Redirect to='/messaging' />}
        />
        {/* Redirect /calendar to /build (preserving sub-paths like /calendar/A{id}/{date}) */}
        <Route
          path='/calendar'
          render={({ location }) => <Redirect to={location.pathname.replace(/^\/calendar/, '/build')} />}
        />
        {/* Handle all sports-science and ams redirects */}
        <Route
          path={['/sports-science/:path*', '/ams/:path*']}
          render={({ match }) => {
            const pathSegments = match.params.path
              ? match.params.path.split('/')
              : [];
            const firstSegment = pathSegments[0];

            // If this is a known redirect, use the mapped value
            if (REDIRECT_MAPPING[firstSegment]) {
              return (
                <Redirect
                  to={`/ams/${REDIRECT_MAPPING[firstSegment]}${
                    pathSegments.slice(1).length > 0
                      ? `/${pathSegments.slice(1).join('/')}`
                      : ''
                  }`}
                  status={301}
                />
              );
            }

            // For all other paths, just replace sports-science with ams
            if (match.path.startsWith('/sports-science')) {
              return (
                <Redirect
                  to={`/ams/${match.params.path || ''}`}
                  status={301}
                />
              );
            }

            // '/ams' path that doesn't need redirection
            return null;
          }}
        />

        {/* Root sports-science redirect for landing page */}
        <Route
          exact
          path='/sports-science'
          render={() => <Redirect to='/ams' status={301} />}
        />
        {/* Redirect view only dashboard from Sport Coach Dashboard URL to new KPI Tracker URL */}
        <Route
          path='/sc-dash'
          render={({ location }) => (
            <Redirect
              to={{
                pathname: '/kpi-tracker',
                search: location.search,
              }}
              status={301}
            />
          )}
        />
        <Route path='/shared-workouts' component={SharedWorkout} />
        <Route path='*' component={FourOhFourRoute} />
      </Switch>
    </ConnectedRouter>
  );
};

Routes.propTypes = {
  flags: PropTypes.shape({
    aiProgramImport: PropTypes.bool,
    calendarV2: PropTypes.bool,
    dailyBrief: PropTypes.bool,
    homeNavigator: PropTypes.bool,
    feed: PropTypes.bool,
    journal: PropTypes.bool,
    planner: PropTypes.bool,
    registration: PropTypes.bool,
  }).isRequired,
};

export default withLDConsumer()(Routes);
